import { getSupplier, type SupplierPublicVisibility } from "./suppliers";

export type ProductCategoryId = "steel-metals" | "minerals-ferroalloys" | "agriculture-fresh-produce" | "pumps-industrial-equipment";
export type VerificationStatus = "verified" | "partially-verified" | "needs-review";
export type AvailabilityStatus = "network-access" | "available-on-request";
export type ProductImageType = "official" | "generated-family";

export type ProductSpecification = {
  label: string;
  value: string;
  sourceUrl: string;
};

export type Product = {
  id: string;
  slug: string;
  publicName: string;
  internalSupplierId: string;
  brand: string;
  sector: string;
  categoryId: ProductCategoryId;
  category: string;
  subcategory: string;
  shortDescription: string;
  specifications: ProductSpecification[];
  variants: string[];
  applications: string[];
  sourceUrl: string;
  sourceWebsite: string;
  lastVerifiedAt: string;
  verificationStatus: VerificationStatus;
  availabilityStatus: AvailabilityStatus;
  manufacturerVisibility: SupplierPublicVisibility;
  image: string;
  imageAlt: string;
  imageType: ProductImageType;
  imageSourceKey: string;
  featured: boolean;
  rfqEnabled: boolean;
  searchTags: string[];
};

type ProductSeed = Pick<Product, "slug" | "publicName" | "internalSupplierId" | "categoryId" | "category" | "subcategory" | "shortDescription" | "sourceUrl" | "image" | "imageAlt" | "imageSourceKey"> & Partial<Omit<Product, "id" | "brand" | "sector" | "sourceWebsite" | "lastVerifiedAt">>;

function createProduct(seed: ProductSeed): Product {
  const supplier = getSupplier(seed.internalSupplierId);
  if (!supplier) throw new Error(`Unknown supplier: ${seed.internalSupplierId}`);

  return {
    id: `product-${seed.slug}`,
    brand: supplier.name,
    sector: supplier.sector,
    sourceWebsite: supplier.website,
    lastVerifiedAt: "2026-08-30",
    specifications: [],
    variants: [],
    applications: [],
    verificationStatus: "verified",
    availabilityStatus: "network-access",
    manufacturerVisibility: supplier.publicVisibility,
    imageType: "official",
    featured: false,
    rfqEnabled: true,
    searchTags: [],
    ...seed,
  };
}

const atavitaCategoryUrls = {
  fruit: "https://www.atavita.com/productcategory/%D9%85%DB%8C%D9%88%D9%87%E2%80%8C-%D9%87%D8%A7%DB%8C-%D8%A7%D8%B3%D8%AA%D9%88%D8%A7%DB%8C%DB%8C",
  greenhouse: "https://www.atavita.com/productcategory/%D8%B5%DB%8C%D9%81%DB%8C-%D8%AC%D8%A7%D8%AA",
  leafy: "https://www.atavita.com/productcategory/%D8%B3%D8%A8%D8%B2%DB%8C%D8%AC%D8%A7%D8%AA",
} as const;

const atavitaProducts = [
  ["blueberry", "Blueberry", "Fruit", "blueberry.webp", "atavita-blueberry", atavitaCategoryUrls.fruit],
  ["dragon-fruit", "Dragon Fruit", "Fruit", "dragon-fruit.webp", "atavita-dragon-fruit", atavitaCategoryUrls.fruit],
  ["banana", "Banana", "Fruit", "banana.webp", "atavita-banana", atavitaCategoryUrls.fruit],
  ["passion-fruit", "Passion Fruit", "Fruit", "passion-fruit.webp", "atavita-passion-fruit", atavitaCategoryUrls.fruit],
  ["strawberry", "Strawberry", "Fruit", "strawberry.webp", "atavita-strawberry", atavitaCategoryUrls.fruit],
  ["green-bell-pepper", "Green Bell Pepper", "Greenhouse Produce", "green-bell-pepper.webp", "atavita-green-pepper", atavitaCategoryUrls.greenhouse],
  ["globe-eggplant", "Globe Eggplant", "Greenhouse Produce", "globe-eggplant.webp", "atavita-eggplant", atavitaCategoryUrls.greenhouse],
  ["cluster-tomato", "Cluster Tomato", "Greenhouse Produce", "cluster-tomato.webp", "atavita-cluster-tomato", atavitaCategoryUrls.greenhouse],
  ["pencil-cucumber", "Pencil Cucumber", "Greenhouse Produce", "pencil-cucumber.webp", "atavita-pencil-cucumber", atavitaCategoryUrls.greenhouse],
  ["colored-bell-pepper", "Colored Bell Pepper", "Greenhouse Produce", "colored-bell-pepper.webp", "atavita-colored-pepper", atavitaCategoryUrls.greenhouse],
  ["guanita-cluster-tomato", "Guanita Cluster Tomato", "Greenhouse Produce", "guanita-cluster-tomato.webp", "atavita-guanita", atavitaCategoryUrls.greenhouse],
  ["rainbow-cherry-tomatoes", "Rainbow Cherry Tomatoes", "Greenhouse Produce", "rainbow-cherry-tomatoes.webp", "atavita-rainbow-tomatoes", atavitaCategoryUrls.greenhouse],
  ["roma-tomato", "Roma Tomato", "Greenhouse Produce", "roma-tomato.webp", "atavita-roma-tomato", atavitaCategoryUrls.greenhouse],
  ["beef-tomato", "Beef Tomato", "Greenhouse Produce", "beef-tomato.webp", "atavita-beef-tomato", atavitaCategoryUrls.greenhouse],
  ["swiss-chard", "Swiss Chard", "Leafy Vegetables", "swiss-chard.webp", "atavita-swiss-chard", atavitaCategoryUrls.leafy],
  ["baby-spinach", "Baby Spinach", "Leafy Vegetables", "baby-spinach.webp", "atavita-baby-spinach", atavitaCategoryUrls.leafy],
  ["french-lettuce", "French Lettuce", "Leafy Vegetables", "french-lettuce.webp", "atavita-french-lettuce", atavitaCategoryUrls.leafy],
] as const;

const agriculture = atavitaProducts.map(([slug, publicName, subcategory, , , sourceUrl]) => createProduct({
  slug,
  publicName,
  internalSupplierId: "atavita",
  categoryId: "agriculture-fresh-produce",
  category: "Agriculture & Fresh Produce",
  subcategory,
  shortDescription: `${subcategory} listed in Atavita's current official product catalog.`,
  sourceUrl,
  image: "/images/catalog/produce-clean.webp",
  imageAlt: `${publicName} fresh produce`,
  imageSourceKey: "catalog-produce",
  imageType: "generated-family",
  searchTags: ["Atavita", "fresh produce", "greenhouse"],
  featured: slug === "dragon-fruit" || slug === "green-bell-pepper",
}));

const afaProducts = [
  createProduct({
    slug: "construction-rebar-aii",
    publicName: "AII Construction Rebar",
    internalSupplierId: "afa-steel",
    categoryId: "steel-metals",
    category: "Steel & Metals",
    subcategory: "Construction Rebar",
    shortDescription: "Spiral-ribbed AII construction rebar available through a selected Iranian producer.",
    variants: ["8 mm", "10 mm"],
    sourceUrl: "https://afasteel.com/products/category",
    image: "/images/catalog/rebar-clean.webp",
    imageAlt: "Bundled reinforcing steel rebar",
    imageSourceKey: "catalog-rebar",
    imageType: "generated-family",
    featured: true,
    searchTags: ["AII", "reinforcing bar", "steel"],
  }),
  createProduct({
    slug: "construction-rebar-aiii",
    publicName: "AIII Construction Rebar",
    internalSupplierId: "afa-steel",
    categoryId: "steel-metals",
    category: "Steel & Metals",
    subcategory: "Construction Rebar",
    shortDescription: "Herringbone-ribbed AIII construction rebar in officially listed diameters.",
    variants: ["10 mm", "12 mm", "14 mm", "16 mm", "18 mm", "20 mm", "22 mm", "25 mm", "28 mm", "32 mm"],
    sourceUrl: "https://afasteel.com/products/category",
    image: "/images/catalog/rebar-clean.webp",
    imageAlt: "Bundled reinforcing steel rebar",
    imageSourceKey: "catalog-rebar",
    imageType: "generated-family",
    searchTags: ["AIII", "reinforcing bar", "steel"],
  }),
  createProduct({
    slug: "afa-steel-billet",
    publicName: "Steel Billet",
    internalSupplierId: "afa-steel",
    categoryId: "steel-metals",
    category: "Steel & Metals",
    subcategory: "Semi-Finished Steel",
    shortDescription: "Steel billet listed in the producer's current official product categories.",
    sourceUrl: "https://afasteel.com/products/category/2",
    image: "/images/catalog/billet-clean.webp",
    imageAlt: "Stacked square steel billets",
    imageSourceKey: "catalog-billet",
    imageType: "generated-family",
    searchTags: ["billet", "semi-finished steel"],
  }),
];

const zafarProducts = [
  createProduct({
    slug: "zafar-rebar",
    publicName: "Rebar",
    internalSupplierId: "zafar-steel",
    categoryId: "steel-metals",
    category: "Steel & Metals",
    subcategory: "Construction Rebar",
    shortDescription: "Reinforcing steel product listed on the current official product page.",
    sourceUrl: "https://zafarsteel.ir/rebar/",
    image: "/images/catalog/rebar-clean.webp",
    imageAlt: "Bundled reinforcing steel rebar",
    imageSourceKey: "catalog-rebar",
    imageType: "generated-family",
    searchTags: ["reinforcing bar", "steel"],
  }),
  createProduct({
    slug: "semi-light-i-beam",
    publicName: "Semi-Light I-Beam",
    internalSupplierId: "zafar-steel",
    categoryId: "steel-metals",
    category: "Steel & Metals",
    subcategory: "Structural Steel",
    shortDescription: "Semi-light steel I-beam documented on the manufacturer's official product page.",
    specifications: [{ label: "Documented standard", value: "Iranian National Standard No. 16348", sourceUrl: "https://zafarsteel.ir/iron-beam/" }],
    sourceUrl: "https://zafarsteel.ir/iron-beam/",
    image: "/images/products/steel/semi-light-i-beam.webp",
    imageAlt: "Stacked semi-light steel I-beams",
    imageSourceKey: "zafar-i-beam",
    searchTags: ["I-beam", "structural steel", "16348"],
  }),
  createProduct({
    slug: "zafar-steel-billet",
    publicName: "Steel Billet",
    internalSupplierId: "zafar-steel",
    categoryId: "steel-metals",
    category: "Steel & Metals",
    subcategory: "Semi-Finished Steel",
    shortDescription: "Square steel billet offered in the grades documented by the manufacturer.",
    variants: ["3SP", "5SP"],
    sourceUrl: "https://zafarsteel.ir/bullion/",
    image: "/images/catalog/billet-clean.webp",
    imageAlt: "Stacked square steel billets",
    imageSourceKey: "catalog-billet",
    imageType: "generated-family",
    featured: true,
    searchTags: ["billet", "3SP", "5SP"],
  }),
  createProduct({
    slug: "rolling-mill-rolls",
    publicName: "Rolling Mill Rolls",
    internalSupplierId: "zafar-steel",
    categoryId: "steel-metals",
    category: "Steel & Metals",
    subcategory: "Rolling Equipment",
    shortDescription: "Cast rolls for steel rolling operations, with documented material families.",
    variants: ["Chilled ductile iron", "Pearlitic chromium-molybdenum", "Acicular-bainitic", "Acicular-martensitic nickel-molybdenum"],
    applications: ["Steel rolling", "Casting", "Profile production"],
    sourceUrl: "https://zafarsteel.ir/roller-skating/",
    image: "/images/products/steel/rolling-mill-rolls.webp",
    imageAlt: "Industrial rolling mill rolls",
    imageSourceKey: "zafar-rolling-rolls",
    searchTags: ["mill roll", "rolling", "cast roll"],
  }),
];

const partGowalGroups = [
  {
    categoryId: "minerals-ferroalloys" as const,
    category: "Minerals & Ferroalloys",
    subcategory: "Ferroalloys & Smelting Materials",
    sourceBase: "https://partgowal.com/en/ferroalloys",
    image: "/images/products/minerals/ferroalloys-family.webp",
    imageSourceKey: "part-ferroalloys",
    items: [
      ["high-carbon-ferrochrome", "High-Carbon Ferrochrome", "7"],
      ["low-carbon-ferrochrome", "Low-Carbon Ferrochrome", "8"],
      ["high-carbon-ferromanganese", "High-Carbon Ferromanganese", "9"],
      ["medium-carbon-ferromanganese", "Medium-Carbon Ferromanganese", "10"],
      ["low-carbon-ferromanganese", "Low-Carbon Ferromanganese", "11"],
      ["ferromolybdenum", "Ferromolybdenum", "12"],
      ["ferrovanadium", "Ferrovanadium", "13"],
      ["ferrotitanium", "Ferrotitanium", "14"],
      ["ferrosilicon", "Ferrosilicon", "15"],
      ["ferrosilicomanganese", "Ferrosilicomanganese", "16"],
      ["nickel", "Nickel", "28"],
      ["copper-cathode", "Copper Cathode", "29"],
    ],
  },
  {
    categoryId: "minerals-ferroalloys" as const,
    category: "Minerals & Ferroalloys",
    subcategory: "Ores & Concentrates",
    sourceBase: "https://partgowal.com/en/ores",
    image: "/images/products/minerals/ores-concentrates-family.webp",
    imageSourceKey: "part-ores",
    items: [
      ["chromite-ore", "Chromite Ore", "23"],
      ["copper-ore", "Copper Ore", "24"],
      ["antimony-ore", "Antimony Ore", "30"],
      ["fluorite-ore", "Fluorite Ore", "31"],
      ["copper-concentrate", "Copper Concentrate", "19"],
      ["antimony-concentrate", "Antimony Concentrate", "20"],
      ["chromite-concentrate", "Chromite Concentrate", "33"],
      ["fluorite-concentrate", "Fluorite Concentrate", "34"],
    ],
  },
  {
    categoryId: "steel-metals" as const,
    category: "Steel & Metals",
    subcategory: "Iron & Steel Materials",
    sourceBase: "https://partgowal.com/en/steel-products",
    image: "/images/products/steel/steel-materials-family.webp",
    imageSourceKey: "part-steel",
    items: [
      ["iron-concentrate", "Iron Concentrate", "21"],
      ["cast-iron-ingot", "Cast Iron Ingot", "26"],
      ["network-rebar", "Rebar", "22"],
      ["network-billet", "Billet", "27"],
      ["agglomerate", "Agglomerate", "32"],
    ],
  },
  {
    categoryId: "minerals-ferroalloys" as const,
    category: "Minerals & Ferroalloys",
    subcategory: "Carbon Materials",
    sourceBase: "https://partgowal.com/en/carbon-products",
    image: "/images/products/minerals/carbon-materials-family.webp",
    imageSourceKey: "part-carbon",
    items: [
      ["cpc-high-sulfur-graphite", "High-Sulfur Graphite (CPC)", "17"],
      ["gpc-low-sulfur-graphite", "Low-Sulfur Graphite (GPC)", "18"],
      ["coke", "Coke", "45"],
    ],
  },
] as const;

const partGowalProducts = partGowalGroups.flatMap((group) => group.items.map(([slug, publicName, anchor]) => createProduct({
  slug,
  publicName,
  internalSupplierId: "part-gowal",
  categoryId: group.categoryId,
  category: group.category,
  subcategory: group.subcategory,
  shortDescription: `${publicName} listed in the network producer's current official ${group.subcategory.toLowerCase()} range.`,
  sourceUrl: `${group.sourceBase}#p-${anchor}`,
  image: group.image,
  imageAlt: `${group.subcategory} product-family material sample`,
  imageType: "generated-family",
  imageSourceKey: group.imageSourceKey,
  featured: slug === "high-carbon-ferrochrome" || slug === "copper-concentrate",
  searchTags: [group.subcategory, "industrial raw material"],
})));

const navidMotorProducts = [
  createProduct({
    slug: "circulation-pump-1450rpm",
    publicName: "Circulation Pump 1450 rpm",
    internalSupplierId: "navid-motor",
    categoryId: "pumps-industrial-equipment",
    category: "Pumps & Industrial Equipment",
    subcategory: "Circulation Pumps",
    shortDescription: "Circulation pump family for heating, closed hot-water circuits and HVAC systems.",
    variants: ["A6", "A7", "PD37", "PD38", "PD40", "AA2", "HV", "S100"],
    applications: ["Heating systems", "Closed hot-water circuits", "Building services", "HVAC"],
    sourceUrl: "https://navidmotor.com/product/%d8%a7%d9%84%da%a9%d8%aa%d8%b1%d9%88%d9%be%d9%85%d9%be-%d8%b3%db%8c%d8%b1%da%a9%d9%88%d9%84%d8%a7%d8%aa%d9%88%d8%b1-1450rpm/",
    image: "/images/catalog/circulation-pump-clean.webp",
    imageAlt: "Navid Motor circulation pump family",
    imageSourceKey: "catalog-circulation-pump",
    imageType: "generated-family",
    featured: true,
    searchTags: ["circulator", "HVAC", "1450 rpm"],
  }),
  createProduct({
    slug: "etabloc-pump",
    publicName: "E-Tabloc Pump",
    internalSupplierId: "navid-motor",
    categoryId: "pumps-industrial-equipment",
    category: "Pumps & Industrial Equipment",
    subcategory: "Close-Coupled Pumps",
    shortDescription: "Close-coupled pump family for water supply, central heating, HVAC and industrial services.",
    variants: ["1450 rpm", "2900 rpm"],
    applications: ["Water supply", "HVAC", "Industrial services", "Agriculture", "Water and wastewater"],
    sourceUrl: "https://navidmotor.com/category-product/etabloc-pump/",
    image: "/images/catalog/circulation-pump-clean.webp",
    imageAlt: "Navid Motor E-Tabloc pump family",
    imageSourceKey: "catalog-circulation-pump",
    imageType: "generated-family",
    searchTags: ["close-coupled pump", "etabloc"],
  }),
  createProduct({
    slug: "garden-pump-ch",
    publicName: "CH Garden Pump",
    internalSupplierId: "navid-motor",
    categoryId: "pumps-industrial-equipment",
    category: "Pumps & Industrial Equipment",
    subcategory: "Garden Pumps",
    shortDescription: "CH-series electropump family for water transfer and small agricultural, residential and industrial duties.",
    variants: ["CH160", "CH210", "CH310", "CHT160", "CHT210", "CHT310"],
    applications: ["Agricultural pool transfer", "Building pressure boosting", "Drip irrigation", "Fire-water systems"],
    sourceUrl: "https://navidmotor.com/product/%d8%a7%d9%84%da%a9%d8%aa%d8%b1%d9%88%d9%be%d9%85%d9%be-%d8%a8%d8%a7%d8%ba%db%8c-ch/",
    image: "/images/catalog/circulation-pump-clean.webp",
    imageAlt: "Navid Motor CH garden pump",
    imageSourceKey: "catalog-circulation-pump",
    imageType: "generated-family",
    searchTags: ["CH", "garden pump", "irrigation"],
  }),
  createProduct({
    slug: "domestic-pump-cab",
    publicName: "CAB Domestic Pump",
    internalSupplierId: "navid-motor",
    categoryId: "pumps-industrial-equipment",
    category: "Pumps & Industrial Equipment",
    subcategory: "Domestic Pumps",
    shortDescription: "CAB-series domestic electropump family for shallow-water transfer and building pressure boosting.",
    variants: ["CAB150", "CABT150", "CAB200", "CABT200", "CAB300", "CABT300"],
    applications: ["Shallow-well transfer", "Pool and tank transfer", "Building pressure boosting", "Small agricultural duties"],
    sourceUrl: "https://navidmotor.com/product/%d8%a7%d9%84%da%a9%d8%aa%d8%b1%d9%88%d9%be%d9%85%d9%be-%d8%ae%d8%a7%d9%86%da%af%db%8c-cab/",
    image: "/images/catalog/circulation-pump-clean.webp",
    imageAlt: "Navid Motor CAB domestic pump",
    imageSourceKey: "catalog-circulation-pump",
    imageType: "generated-family",
    searchTags: ["CAB", "domestic pump", "booster"],
  }),
  createProduct({
    slug: "submersible-drainage-pump",
    publicName: "Submersible Drainage Pump",
    internalSupplierId: "navid-motor",
    categoryId: "pumps-industrial-equipment",
    category: "Pumps & Industrial Equipment",
    subcategory: "Drainage Pumps",
    shortDescription: "Submersible drainage pump family for clear-water drainage in buildings, agriculture and industrial pits.",
    variants: ["NM X 65-4/3B", "NM X 50-3/2B", "NM X 34-2/1.5B", "NM X 18-1/1B"],
    applications: ["Building drainage", "Construction pits", "Shallow-water transfer", "Industrial drainage"],
    sourceUrl: "https://navidmotor.com/category-product/%d8%a7%d9%84%da%a9%d8%aa%d8%b1%d9%88%d9%be%d9%85%d9%be-%da%a9%d9%81-%da%a9%d8%b4/",
    image: "/images/catalog/submersible-pump-clean.webp",
    imageAlt: "Navid Motor submersible drainage pump family",
    imageSourceKey: "catalog-submersible-pump",
    imageType: "generated-family",
    featured: true,
    searchTags: ["drainage", "submersible pump"],
  }),
  createProduct({
    slug: "vertical-high-pressure-pump",
    publicName: "Vertical High-Pressure Pump",
    internalSupplierId: "navid-motor",
    categoryId: "pumps-industrial-equipment",
    category: "Pumps & Industrial Equipment",
    subcategory: "Multistage Pumps",
    shortDescription: "Vertical multistage pump family for pressure boosting, water supply, irrigation and fire-water systems.",
    variants: ["2900 rpm", "1450 rpm", "NM H X 2-2/05", "NM H X 2-3/1", "NM H X 2-4/1", "NM H X 2-5/1"],
    applications: ["Water supply", "Industrial services", "Irrigation", "Fire-water systems", "Water treatment"],
    sourceUrl: "https://navidmotor.com/category-product/centrifugal-pumps/",
    image: "/images/catalog/circulation-pump-clean.webp",
    imageAlt: "Navid Motor vertical high-pressure pump family",
    imageSourceKey: "catalog-circulation-pump",
    imageType: "generated-family",
    searchTags: ["multistage", "high-pressure", "WKL"],
  }),
  createProduct({
    slug: "filtered-pool-pump",
    publicName: "Filtered Pool Pump",
    internalSupplierId: "navid-motor",
    categoryId: "pumps-industrial-equipment",
    category: "Pumps & Industrial Equipment",
    subcategory: "Pool Pumps",
    shortDescription: "Filtered pool electropump family for water circulation and filtration in pools and spas.",
    variants: ["SFP100", "SFP150", "SFP200", "SFP300", "SFPT150", "SFPT200", "SFPT300", "STP100", "STP150", "STP200", "STPT100", "STPT150", "STPT200"],
    applications: ["Pool circulation", "Spa circulation", "Water filtration", "Small agricultural water circulation"],
    sourceUrl: "https://navidmotor.com/product/%d8%a7%d9%84%da%a9%d8%aa%d8%b1%d9%88%d9%be%d9%85%d9%be-%d9%81%db%8c%d9%84%d8%aa%d8%b1%d8%af%d8%a7%d8%b1-%d8%a7%d8%b3%d8%aa%d8%ae%d8%b1%db%8c/",
    image: "/images/catalog/circulation-pump-clean.webp",
    imageAlt: "Navid Motor filtered pool pump",
    imageSourceKey: "catalog-circulation-pump",
    imageType: "generated-family",
    searchTags: ["pool pump", "SFP", "STP"],
  }),
];

const pumpiranCatalogue = "https://pumpiran.co/EN/wp-content/uploads/2017/CATALOGUE/General-catalogue.pdf";
const pumpiranSeeds = [
  ["pumpiran-centrifugal-pumps", "Centrifugal Pumps", "Centrifugal Pumps", "24", "For irrigation, municipal and industrial water supply, plus cooling and heating circulation systems.", "Output size: 32–300 mm; capacity: 5–1,650 m³/h; head: 5–90 m; operating temperature: -10°C to 110°C; test pressure: 16 bar", "centrifugal"],
  ["pumpiran-high-delivery-centrifugal", "High-Delivery Centrifugal Pumps", "EA / ER", "25", "For irrigation, municipal and industrial water supply, plus cooling and heating circulation systems.", "Output size: 100–300 mm; capacity: 40–1,800 m³/h; head: 5–90 m; operating temperature: -10°C to 110°C; test pressure: 12 bar", "centrifugal"],
  ["pumpiran-double-suction-dsp", "Double-Suction Electropumps", "DSP", "26", "For water works, irrigation and drainage pumping stations, industrial water supply, fire-water and marine applications.", "Output size: up to 700 mm; capacity: up to 9,000 m³/h; head: up to 190 m; operating temperature: up to 140°C; test pressure: 30 bar", "centrifugal"],
  ["pumpiran-mdsp", "Multistage Double-Suction Pumps", "MDSP", "27", "For raw, clean and service water, including seawater duties.", "Output size: 300–500 mm; capacity: up to 12,000 m³/h; head: up to 550 m; operating temperature: up to 80°C; test pressure: 70 bar", "centrifugal"],
  ["pumpiran-dspv", "Vertical Double-Suction Electropumps", "DSPV", "28", "For water works, irrigation and drainage pumping stations, industrial water supply and fire-water systems.", "Output size: up to 700 mm; capacity: up to 9,000 m³/h; head: up to 190 m; operating temperature: up to 140°C; test pressure: 30 bar", "centrifugal"],
  ["pumpiran-wkl", "High-Pressure Centrifugal Pumps", "WKL", "30", "For municipal and industrial water supply, pressure boosting and irrigation sprinkler systems.", "Output size: 32–150 mm; capacity: 2–440 m³/h; head: up to 300 m; operating temperature: -10°C to 140°C; test pressure: 40 bar", "centrifugal"],
  ["pumpiran-mc-cv", "High-Pressure MC & CV Pumps", "MC / CV", "31", "For water supply, pressure boosting, irrigation, boiler feed and cooling or hot-water circulation.", "Output size: up to 300 mm; capacity: up to 1,200 m³/h; head: up to 510 m; operating temperature: -10°C to 140°C; test pressure: 75 bar", "centrifugal"],
  ["pumpiran-md-high-pressure", "MD High-Pressure Pumps", "MD", "32", "For clear-water transport, water supply and pressure-boosting installations.", "Output size: 50–150 mm; capacity: up to 200 m³/h; head: 50–700 m; operating temperature: up to 80°C; test pressure: 100 bar", "centrifugal"],
  ["pumpiran-pcr", "Vertical Multistage In-Line Pumps", "PCR", "33", "For pressure boosting in buildings and industrial water supply.", "Output size: 25–125 mm; capacity: up to 180 m³/h; head: up to 275 m; operating temperature: -15°C to 150°C", "centrifugal"],
  ["pumpiran-submersible-pumps", "Submersible Pumps", "Submersible", "36", "For clean or slightly contaminated water in general water supply and irrigation.", "Output size: 1½–8 in; capacity: up to 650 m³/h; head: up to 600 m; test pressure: 65 bar", "submersible"],
  ["pumpiran-single-phase-submersible", "Single-Phase Submersible Electropumps", "Single Phase", "37", "For clean or slightly contaminated water in water supply, irrigation and sprinkler systems.", "Output size: 1¼–2 in; capacity: up to 24 m³/h; head: up to 263 m; test pressure: 39 bar", "submersible"],
  ["pumpiran-open-well", "Vertical Open-Well Pumps", "Open Well", "38", "For sump drainage, open wells and surface-water collection.", "Output size: 32–80 mm; capacity: up to 180 m³/h; head: up to 180 m; test pressure: 30 bar", "submersible"],
  ["pumpiran-heavy-duty-sludge", "Heavy-Duty Sludge Pumps", "HDS", "39", "For difficult materials including drilling mud and clay-bearing water.", "Output size: 50–150 mm; capacity: up to 350 m³/h; head: up to 70 m; operating temperature: -10°C to 110°C; test pressure: 10 bar", "centrifugal"],
] as const;

const pumpiranProducts = pumpiranSeeds.map(([slug, publicName, family, page, shortDescription, range, visual]) => createProduct({
  slug,
  publicName,
  internalSupplierId: "pumpiran",
  categoryId: "pumps-industrial-equipment",
  category: "Pumps & Industrial Equipment",
  subcategory: family,
  shortDescription,
  specifications: [{ label: `Official catalogue range · page ${page}`, value: range, sourceUrl: pumpiranCatalogue }],
  variants: [family],
  sourceUrl: pumpiranCatalogue,
  image: visual === "submersible" ? "/images/catalog/submersible-pump-clean.webp" : "/images/catalog/circulation-pump-clean.webp",
  imageAlt: visual === "submersible" ? "Pumpiran submersible pump family" : "Pumpiran centrifugal pump family",
  imageSourceKey: visual === "submersible" ? "catalog-submersible-pump" : "catalog-circulation-pump",
  imageType: "generated-family",
  featured: slug === "pumpiran-double-suction-dsp" || slug === "pumpiran-submersible-pumps",
  searchTags: [family, "Pumpiran", "pump"],
}));

export const reviewProducts: Product[] = [
  ["navid-sahand-bb1", "BB1 Pump", "https://navidsahandpump.com/en/product/bb1-pump/"],
  ["navid-sahand-bb3", "BB3 Pump", "https://navidsahandpump.com/en/product/bb3-pump/"],
  ["navid-sahand-wkl", "WKL Pump", "https://navidsahandpump.com/en/product/wkl-pump/"],
  ["navid-sahand-slurry", "Slurry Pump", "https://navidsahandpump.com/en/product/slurry-pump/"],
  ["navid-sahand-oh1", "OH1 Pump", "https://navidsahandpump.com/en/product/oh1-pump/"],
  ["navid-sahand-oh2", "OH2 Pump", "https://navidsahandpump.com/product/oh2-centrifugal-pump/"],
].map(([slug, publicName, sourceUrl]) => createProduct({
  slug,
  publicName,
  internalSupplierId: "navid-sahand-pump",
  categoryId: "pumps-industrial-equipment",
  category: "Pumps & Industrial Equipment",
  subcategory: "Industrial Pumps",
  shortDescription: "Official product URL found, but the live source was unavailable during final verification.",
  sourceUrl,
  verificationStatus: "partially-verified",
  availabilityStatus: "available-on-request",
  image: "",
  imageAlt: "",
  imageSourceKey: "",
  imageType: "official",
  rfqEnabled: false,
}));

export const products: Product[] = [
  ...agriculture,
  ...afaProducts,
  ...zafarProducts,
  ...partGowalProducts,
  ...navidMotorProducts,
  ...pumpiranProducts,
  ...reviewProducts,
];

export const publicProducts = products.filter((product) => product.verificationStatus === "verified" && product.rfqEnabled);
export const featuredProducts = publicProducts.filter((product) => product.featured).slice(0, 10);

export const productCategories = [
  { id: "all", label: "All" },
  { id: "steel-metals", label: "Steel & Metals" },
  { id: "minerals-ferroalloys", label: "Minerals & Ferroalloys" },
  { id: "agriculture-fresh-produce", label: "Agriculture & Fresh Produce" },
  { id: "pumps-industrial-equipment", label: "Pumps & Industrial Equipment" },
] as const;

export function getProductBySlug(slug: string | undefined) {
  return publicProducts.find((product) => product.slug === slug);
}

export function getPublicManufacturerLabel(product: Product) {
  if (product.manufacturerVisibility === "hidden") return null;
  return product.brand;
}

export function getAvailabilityLabel(status: AvailabilityStatus) {
  return status === "network-access" ? "Available Through Our Network" : "Available on Request";
}
